import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserSwitch, Calendar, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useAuthStore } from '@/features/auth/store';
import { fetchUsers } from '@/lib/usersApi';
import {
  fetchDelegations,
  createDelegation,
  revokeDelegation,
  type ClerkDelegation,
} from '@/lib/clerkDelegationsApi';
import { getApiErrorMessage } from '@/lib/api';

const CLERK_TYPES = new Set(['senior_clerk', 'junior_clerk']);
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function ClerkCoverPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.memberType === 'admin';
  const queryClient = useQueryClient();

  const { data: delegations = [], isLoading } = useQuery({
    queryKey: ['clerk-delegations'],
    queryFn: fetchDelegations,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users', 'clerks-active'],
    queryFn: () => fetchUsers({ status: 'active', limit: 100 }),
  });

  const clerks = useMemo(() => users.filter((u) => CLERK_TYPES.has(u.memberType)), [users]);

  // A senior clerk arranges only their OWN cover; an admin picks the delegating clerk.
  const [fromClerkUserId, setFromClerkUserId] = useState<string>(isAdmin ? '' : (user?.id ?? ''));
  const [toClerkUserId, setToClerkUserId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(todayStr());
  const [endDate, setEndDate] = useState<string>(todayStr());
  const [reason, setReason] = useState<string>('');

  const effectiveFrom = isAdmin ? fromClerkUserId : (user?.id ?? '');

  const createMut = useMutation({
    mutationFn: createDelegation,
    onSuccess: () => {
      toast.success('Coverage arranged');
      queryClient.invalidateQueries({ queryKey: ['clerk-delegations'] });
      setToClerkUserId('');
      setReason('');
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not arrange coverage')),
  });

  const revokeMut = useMutation({
    mutationFn: revokeDelegation,
    onSuccess: () => {
      toast.success('Coverage ended');
      queryClient.invalidateQueries({ queryKey: ['clerk-delegations'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not end coverage')),
  });

  const canSubmit =
    !!effectiveFrom && !!toClerkUserId && effectiveFrom !== toClerkUserId && !!startDate && !!endDate && endDate >= startDate;

  const submit = () => {
    if (!canSubmit) return;
    createMut.mutate({ fromClerkUserId: effectiveFrom, toClerkUserId, startDate, endDate, reason: reason || undefined });
  };

  const fromOptions = clerks.map((c) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }));
  const toOptions = clerks
    .filter((c) => c.id !== effectiveFrom)
    .map((c) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }));

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Clerk Cover"
        subtitle="Arrange temporary coverage when a clerk is away — the covering clerk sees their barristers for the window, and access ends automatically."
      />

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
            <div className="lg:col-span-1">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Clerk away</label>
              {isAdmin ? (
                <SearchableSelect
                  value={fromClerkUserId}
                  onChange={setFromClerkUserId}
                  options={fromOptions}
                  placeholder="Select clerk…"
                />
              ) : (
                <div className="flex h-9 items-center rounded-md border border-border bg-muted/30 px-3 text-sm text-foreground">
                  {user ? `${user.firstName} ${user.lastName}` : 'You'}
                </div>
              )}
            </div>
            <div className="lg:col-span-1">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Covered by</label>
              <SearchableSelect
                value={toClerkUserId}
                onChange={setToClerkUserId}
                options={toOptions}
                placeholder="Select clerk…"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
              <input
                type="date"
                value={startDate}
                min={todayStr()}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">To</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
              />
            </div>
            <div className="flex items-end">
              <Button variant="primary" className="w-full" disabled={!canSubmit || createMut.isPending} onClick={submit}>
                <UserSwitch className="mr-1 h-4 w-4" />
                Arrange cover
              </Button>
            </div>
          </div>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional) — e.g. annual leave"
            className="mt-3 h-9 w-full rounded-md border border-border bg-background px-3 text-sm"
          />
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
        </div>
      ) : delegations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserSwitch className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-lg font-medium text-foreground">No cover arranged</p>
            <p className="mt-1 text-sm text-muted-foreground">Arrange cover above when a clerk is going away.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {delegations.map((d) => (
            <DelegationRow key={d.id} d={d} onRevoke={() => revokeMut.mutate(d.id)} revoking={revokeMut.isPending} />
          ))}
        </div>
      )}
    </div>
  );
}

function DelegationRow({ d, onRevoke, revoking }: { d: ClerkDelegation; onRevoke: () => void; revoking: boolean }) {
  const fmt = (s: string) => new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const ended = !!d.revokedAt;
  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-4 p-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <UserSwitch className="h-4 w-4 text-muted-foreground" />
            {d.fromClerkName} <span className="text-muted-foreground">→</span> {d.toClerkName}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {fmt(d.startDate)} – {fmt(d.endDate)}
            </span>
            {d.reason && <span>· {d.reason}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {ended ? (
            <Badge variant="outline">Ended</Badge>
          ) : d.active ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="warning">Scheduled</Badge>
          )}
          {!ended && (
            <Button variant="outline" size="sm" onClick={onRevoke} disabled={revoking}>
              <XCircle className="mr-1 h-4 w-4" />
              End
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
